// Compare's season, week by week (SCENARIOS E3, B-376): every player's
// points on one chart, and a table with each week's players together.
//
// THE WEEK IS THE UNIT. A manager weighing two players asks what each did in
// the same week, so a week's players sit in rows one under the other, the
// week's name once beside them, and the next week below: two players are two
// rows a week, four are four. A column per player would put each player's
// stats in a block of its own, and a receiver's five stat columns times four
// players do not fit a phone; a row per player keeps the stat columns one set
// wide at any width. The chart above says the shape of the season at a look,
// and the table holds every number, so nothing lives only in the chart.
//
// EACH PLAYER IS A COLOR AND A SHAPE, the same in the heads, the chart, its
// readout and the table, and always beside his name, so neither the color nor
// the shape is ever the only way to tell them apart (UX-28).
//
// THE NUMBERS BESIDE EACH OTHER, NEVER A PICK (cohesion check 9): the higher
// points in a week are bold and in the accent, the server's mark
// (`CompareView.weeks[].highest`), and nothing says whom to choose.
//
// DETAIL LEVELS, as the card's game log: Simple shows each week's opponent
// and points; Standard adds the stat columns, the union of the players' own
// sheets (a receiver's targets and catches beside a back's carries), and
// Everything their rates and the rest (B-517).
//
// A PLAYER'S CELLS ARE HIS POSITION'S (B-515): under another's column his
// cell is empty — a kicker has no passing yards to be a dash or a zero —
// and two columns with one short name (a passer's INT and a defense's) are
// headed by their whole names. Points read to a tenth for everybody.

import type { CompareView, ComparePlayer, CompareWeek } from '@fsp/domain';
import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useAtLeast, useDetail } from '../../data/detail';
import { TableScroll } from '../../kit/TableScroll';
import { Caption } from '../../kit/ui';
import { Term } from '../../kit/Why';
import { clubLabel } from '../../sports/registry';
import { columnsAt, statCellText } from '../shared/StatsTable';

/** A player's place in the comparison: his color and his shape. */
export function Marker({ series, size = 12 }: { series: number; size?: number }) {
  const h = size / 2;
  const shape =
    series === 1 ? (
      <rect x={1.5} y={1.5} width={size - 3} height={size - 3} />
    ) : series === 2 ? (
      <polygon points={`${h},1 ${size - 1},${size - 1.5} 1,${size - 1.5}`} />
    ) : series === 3 ? (
      <polygon points={`${h},0.5 ${size - 0.5},${h} ${h},${size - 0.5} 0.5,${h}`} />
    ) : (
      <circle cx={h} cy={h} r={h - 1.5} />
    );
  return (
    <svg
      className="fsp-cmp-marker"
      data-series={series}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
    >
      {shape}
    </svg>
  );
}

/** "vs DAL" or "@ DAL", as a week's opponent reads in a narrow cell. */
function opponentOf(w: CompareWeek | undefined): string {
  if (w === undefined || w.opponent === null) return '';
  return `${w.home === false ? '@' : 'vs'} ${clubLabel(w.opponent)}`;
}

/**
 * What a week with no points says in their place: never a 0 that reads as a
 * bad game. A week he is not in has begun and his game has not: a Sunday
 * player on a Thursday night.
 */
function noPoints(w: CompareWeek | undefined): string {
  if (w === undefined) return 'Yet to play';
  switch (w.state) {
    case 'bye':
      return 'Bye';
    case 'no-line':
      return 'No stats';
    case 'pending':
      return 'Stats to come';
    default:
      return '—';
  }
}

const points = (w: CompareWeek | undefined): number | null =>
  w !== undefined && w.state === 'played' ? w.points : null;

const fmt = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(1));

/** Points, always to a tenth: a kicker's 9 read as 9.0 beside a back's 12.4. */
const pts = (v: number): string => v.toFixed(1);

/** "Wk 5" from "Week 5"; any other label as it is. */
const shortWeek = (label: string) => label.replace(/^Week\s+/i, 'Wk ');

/**
 * The chart and the table, or one of them: from a desktop up the chart sits
 * beside the figures and the table runs the whole width under both (the
 * plan of 2026-10-17, §2.2).
 */
export function CompareWeeks({
  view,
  parts = 'both',
}: {
  view: CompareView;
  parts?: 'both' | 'chart' | 'table';
}) {
  if (view.weeks.length === 0) return null;
  return (
    <section
      className="fsp-cmp-weeks"
      aria-label={parts === 'chart' ? 'Points by week' : 'Week by week'}
    >
      {parts !== 'table' ? (
        <>
          <Caption>Points by week</Caption>
          <PointsChart view={view} />
        </>
      ) : null}
      {parts !== 'chart' ? (
        <>
          <Caption>Week by week</Caption>
          <WeekTable view={view} />
        </>
      ) : null}
    </section>
  );
}

// ── The chart ───────────────────────────────────────────────────────────────

/** The box's width, kept as it changes, so the chart is drawn at its own size. */
function useWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const seen = new ResizeObserver(() => setWidth(el.clientWidth));
    seen.observe(el);
    return () => seen.disconnect();
  }, []);
  return [ref, width];
}

/**
 * The axis: the highest week rounded up to a five, and the lowest rounded
 * down when a week went below zero (a defense's, a quarterback's), so a
 * negative week is drawn below the zero line rather than on it.
 */
function axisOf(players: readonly ComparePlayer[]): { top: number; bottom: number } {
  let top = 0;
  let bottom = 0;
  for (const p of players)
    for (const w of p.weeks) {
      const v = points(w);
      if (v === null) continue;
      top = Math.max(top, v);
      bottom = Math.min(bottom, v);
    }
  return { top: Math.max(10, Math.ceil(top / 5) * 5), bottom: Math.floor(bottom / 5) * 5 };
}

/**
 * Every player's points by week, a line each, oldest week on the left. A week
 * with no points is a gap in the line, never a drop to zero. Resting on it, a
 * tap or the arrow keys pick a week, and the line above says each player's
 * points that week; the latest week is said to begin with.
 */
function PointsChart({ view }: { view: CompareView }) {
  const [box, width] = useWidth();
  const weeks = view.weeks;
  const players = view.players;
  // THE LATEST WEEK ANYBODY SCORED IN, to begin with: the week under way
  // often has nobody's points yet, and a reading of dashes says nothing.
  const latest = (() => {
    for (let i = weeks.length - 1; i >= 0; i -= 1) {
      const period = weeks[i]!.period;
      if (players.some((p) => p.weeks.some((w) => w.period === period && points(w) !== null)))
        return i;
    }
    return weeks.length - 1;
  })();
  const [at, setAt] = useState(latest);
  const shown = Math.min(at, weeks.length - 1);
  const height = width < 480 ? 150 : 180;
  const pad = { left: 30, right: 10, top: 10, bottom: 22 };
  const inner = { w: Math.max(0, width - pad.left - pad.right), h: height - pad.top - pad.bottom };
  const { top, bottom } = axisOf(players);
  const x = (i: number) =>
    pad.left + (weeks.length === 1 ? inner.w / 2 : (i * inner.w) / (weeks.length - 1));
  const y = (v: number) => pad.top + inner.h * ((top - v) / (top - bottom));
  const byPeriod = players.map((p) => new Map(p.weeks.map((w) => [w.period, w])));
  // Every other week's number under a narrow chart, so they do not run together.
  const every = inner.w / Math.max(1, weeks.length) < 22 ? 2 : 1;

  const pick = (clientX: number) => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect || weeks.length === 0) return;
    const rel = clientX - rect.left - pad.left;
    const step = weeks.length === 1 ? 1 : inner.w / (weeks.length - 1);
    setAt(Math.max(0, Math.min(weeks.length - 1, Math.round(rel / step))));
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') setAt((a) => Math.max(0, Math.min(a, weeks.length - 1) - 1));
    else if (e.key === 'ArrowRight') setAt((a) => Math.min(weeks.length - 1, a + 1));
    else if (e.key === 'Home') setAt(0);
    else if (e.key === 'End') setAt(weeks.length - 1);
    else return;
    e.preventDefault();
  };
  const week = weeks[shown]!;

  return (
    <div className="fsp-cmp-chart">
      {/* WHAT THE CHOSEN WEEK SAYS, in words and numbers: the chart is a
          picture of the table below, and this line is its reading. */}
      <div className="fsp-cmp-readout" aria-live="polite">
        <span className="fsp-cmp-readout-week">{week.label}</span>
        {players.map((p, i) => {
          const w = byPeriod[i]!.get(week.period);
          const v = points(w);
          const high = week.highest.includes(p.player.id);
          return (
            <span key={p.player.id} className="fsp-cmp-readout-item">
              <Marker series={i} size={10} />
              <span className="fsp-cmp-readout-name">{p.player.shortName}</span>
              <span className="fsp-num" data-high={high || undefined}>
                {v === null ? noPoints(w) : pts(v)}
              </span>
            </span>
          );
        })}
      </div>
      <div
        ref={box}
        className="fsp-cmp-plot"
        tabIndex={0}
        role="group"
        aria-label={`Points by week. ${week.label} is shown; the arrow keys move the week.`}
        onPointerMove={(e) => {
          if (e.pointerType === 'mouse') pick(e.clientX);
        }}
        onPointerDown={(e) => pick(e.clientX)}
        onKeyDown={onKey}
      >
        {width > 0 ? (
          <svg width={width} height={height} aria-hidden className="fsp-cmp-svg">
            {[...(bottom < 0 ? [bottom] : []), 0, top / 2, top].map((v) => (
              <g key={v}>
                <line
                  className="fsp-cmp-grid"
                  x1={pad.left}
                  x2={width - pad.right}
                  y1={y(v)}
                  y2={y(v)}
                />
                <text className="fsp-cmp-axis" x={pad.left - 6} y={y(v) + 4} textAnchor="end">
                  {fmt(v)}
                </text>
              </g>
            ))}
            {weeks.map((w, i) =>
              i % every === (weeks.length - 1) % every ? (
                <text
                  key={w.period}
                  className="fsp-cmp-axis"
                  x={x(i)}
                  y={height - 6}
                  textAnchor="middle"
                  data-on={i === shown || undefined}
                >
                  {w.period}
                </text>
              ) : null,
            )}
            <line
              className="fsp-cmp-guide"
              x1={x(shown)}
              x2={x(shown)}
              y1={pad.top}
              y2={pad.top + inner.h}
            />
            {players.map((p, s) => {
              // A line through the weeks with points, lifted at each gap.
              let d = '';
              let lifted = true;
              weeks.forEach((w, i) => {
                const v = points(byPeriod[s]!.get(w.period));
                if (v === null) {
                  lifted = true;
                  return;
                }
                d += `${lifted ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
                lifted = false;
              });
              return (
                <g key={p.player.id} className="fsp-cmp-series" data-series={s}>
                  <path className="fsp-cmp-line" d={d} />
                  {weeks.map((w, i) => {
                    const v = points(byPeriod[s]!.get(w.period));
                    if (v === null) return null;
                    const size = i === shown ? 11 : 8;
                    return (
                      <g
                        key={w.period}
                        transform={`translate(${(x(i) - size / 2).toFixed(1)},${(y(v) - size / 2).toFixed(1)})`}
                      >
                        <MarkerShape series={s} size={size} />
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        ) : null}
      </div>
    </div>
  );
}

/** A marker's shape alone, inside the chart's own drawing. */
function MarkerShape({ series, size }: { series: number; size: number }) {
  const h = size / 2;
  if (series === 1) return <rect className="fsp-cmp-dot" width={size} height={size} />;
  if (series === 2)
    return <polygon className="fsp-cmp-dot" points={`${h},0 ${size},${size} 0,${size}`} />;
  if (series === 3)
    return <polygon className="fsp-cmp-dot" points={`${h},0 ${size},${h} ${h},${size} 0,${h}`} />;
  return <circle className="fsp-cmp-dot" cx={h} cy={h} r={h} />;
}

// ── The table ───────────────────────────────────────────────────────────────

/**
 * A group of rows a week, newest week on top as the card's game log has it,
 * each player a row in the heads' order; the season's totals last. The
 * week's name spans its rows, and stays in sight with the player's name when
 * the stat columns scroll sideways.
 */
function WeekTable({ view }: { view: CompareView }) {
  const standard = useAtLeast('standard');
  const level = useDetail();
  const players = view.players;
  // EACH PLAYER'S SHEET AT THE READER'S LEVEL (B-517), from Standard: the
  // rates and the rest at Everything, as the card's log reads them.
  const columns = standard ? columnsAt(view.columns, level) : [];
  const byPeriod = players.map((p) => new Map(p.weeks.map((w) => [w.period, w])));
  const weeks = [...view.weeks].reverse();
  const firstBefore = weeks.findIndex((w) => w.beforeSeason);
  const anyBefore = firstBefore > 0;
  const span = 4 + columns.length;
  const seasonHigh = view.highestPoints;
  // ONE SHORT NAME FOR TWO COLUMNS reads as one stat: those are headed by
  // their whole names.
  const shared = new Set(columns.map((c) => c.label).filter((l, i, all) => all.indexOf(l) !== i));
  const headOf = (c: (typeof columns)[number]) => (shared.has(c.label) ? c.name : c.label);
  /** Whether a column is one of his position's (`ComparePlayer.columns`). */
  const his = (s: number, key: string) => players[s]!.columns.includes(key);

  const group = (key: string, label: ReactNode, rows: (s: number) => ReactNode, odd: boolean) =>
    players.map((p, s) => (
      <tr
        key={`${key}-${p.player.id}`}
        className="fsp-cmp-row"
        data-first={s === 0 || undefined}
        data-odd={odd || undefined}
        data-season={key === 'season' || undefined}
      >
        {s === 0 ? (
          <th scope="rowgroup" rowSpan={players.length} className="fsp-cmp-week">
            {/* A box of a set width, so the pinned name beside it is where
                the stylesheet says the week column ends. */}
            <span className="fsp-cmp-week-in">{label}</span>
          </th>
        ) : null}
        <th scope="row" className="fsp-cmp-who">
          <span className="fsp-cmp-who-in">
            <Marker series={s} size={10} />
            <span className="fsp-cmp-who-name">{p.player.shortName}</span>
          </span>
        </th>
        {rows(s)}
      </tr>
    ));

  return (
    <TableScroll className="fsp-log-wrap fsp-cmp-table-wrap">
      <table className="fsp-log fsp-cmp-table">
        <thead>
          <tr>
            <th scope="col" className="fsp-cmp-week">
              <span className="fsp-cmp-week-in">Wk</span>
            </th>
            <th scope="col" className="fsp-cmp-who">
              Player
            </th>
            <th scope="col">Opp</th>
            <th scope="col" className="fsp-log-num">
              <Term tip="Points that week, in this league’s scoring.">Pts</Term>
            </th>
            {columns.map((c) => (
              <th key={c.key} scope="col" className="fsp-log-num">
                <Term tip={c.name.endsWith('.') ? c.name : `${c.name}.`}>{headOf(c)}</Term>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, i) => (
            <Fragment key={week.period}>
              {anyBefore && i === firstBefore ? (
                <tr className="fsp-log-part">
                  <th scope="colgroup" colSpan={span}>
                    Before this league’s season
                  </th>
                </tr>
              ) : null}
              {group(
                String(week.period),
                shortWeek(week.label),
                (s) => {
                  const w = byPeriod[s]!.get(week.period);
                  const v = points(w);
                  const high = week.highest.includes(players[s]!.player.id);
                  return (
                    <>
                      <td className="fsp-cmp-opp">{opponentOf(w)}</td>
                      <td className="fsp-log-num">
                        {v === null ? (
                          <span className="fsp-muted fsp-cmp-none">{noPoints(w)}</span>
                        ) : (
                          <span className="fsp-cmp-pts" data-high={high || undefined}>
                            {pts(v)}
                            {high ? <span className="fsp-sr"> (highest)</span> : null}
                          </span>
                        )}
                      </td>
                      {columns.map((c) => (
                        <td key={c.key} className="fsp-log-num">
                          {v === null || !his(s, c.key) ? '' : statCellText(c, w?.stats)}
                        </td>
                      ))}
                    </>
                  );
                },
                i % 2 === 1,
              )}
            </Fragment>
          ))}
          {/* THE SEASON'S TOTALS, last: games, points and each stat's. */}
          {group(
            'season',
            'Total',
            (s) => {
              const p = players[s]!;
              const high = seasonHigh.includes(p.player.id);
              return (
                <>
                  <td className="fsp-cmp-opp">
                    {p.games} {p.games === 1 ? 'game' : 'games'}
                  </td>
                  <td className="fsp-log-num">
                    {p.points === null ? (
                      <span className="fsp-muted">—</span>
                    ) : (
                      <span className="fsp-cmp-pts" data-high={high || undefined}>
                        {pts(p.points)}
                        {high ? <span className="fsp-sr"> (highest)</span> : null}
                      </span>
                    )}
                  </td>
                  {columns.map((c) => (
                    <td key={c.key} className="fsp-log-num">
                      {!his(s, c.key) ? '' : statCellText(c, p.totals)}
                    </td>
                  ))}
                </>
              );
            },
            weeks.length % 2 === 1,
          )}
        </tbody>
      </table>
    </TableScroll>
  );
}
